import { useEffect, useMemo, useState, useCallback } from 'react';

import { conf } from 'config/env';
import { useSelector } from 'utils/redux';
import useLoading from 'utils/hooks/useLoading';



const options: RequestInit = {
  mode: 'cors' as RequestMode,
  headers: {
    method: 'GET',
    Authorization: `Bearer ${conf.phraseAccessToken}`,
    'Content-Type': 'application/json',
  },
};

const fallback = 'en';

const useTranslations = () => {
  const { on: onLoading, off: offLoading } = useLoading('phraseLoader');

  const [locales, setLocales] = useState([
    { code: 'en', id: null },
    { code: 'fr', id: null },
  ]);

  const [translations, setTranslations] = useState({});
  const [loadedFromOnline, setLoadedFromOnline] = useState({
    en: false,
    fr: false,
  });

  const { locale } = useSelector(({ env }) => ({
    locale: env.locale.substring(0, 2),
  }));

  const getAllLocales = useCallback(async () => {
    const phraseStaticJson = await Promise.all([
      import('locales/phrase/fr-FR.json'),
      import('locales/phrase/en-US.json'),
    ]).then(([phraseFR, phraseEN]) => ({
      fr: phraseFR?.default,
      en: phraseEN?.default,
    }));

    setTranslations(phraseStaticJson);

    try {
      const response = await fetch(
        `https://api.phrase.com/v2/projects/${conf.phraseProjectId}/locales`,
        options
      );
      if (response?.ok) {
        const locales = await response?.json();
        setLocales(locales);
        return locales;
      }
    } catch (e) {
      console.error(
        'Could not load all Phrase locales, using local fallback',
        e,
      );
    }
  }, []);

  const getTranslationsForLocale = useCallback(
    async (locale) => {
      onLoading();
      try {
        const response = await fetch(
          `https://api.phrase.com/v2/projects/${conf.phraseProjectId}/locales/${
            locale.id
          }/download?${new URLSearchParams({
            file_format: 'react_simple_json',
          })}`,
          options
        );
        offLoading();

        if (response?.ok) {
          const data = await response.json();
          return data;
        }
      } catch (e) {
        offLoading();
      }
    },
    [onLoading, offLoading],
  );

  useEffect(() => {
    // Launched once, at start
    // Get all available locales in Phrase project
    (async () => getAllLocales())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const foundTranslation = translations[locale];
      if (!foundTranslation || !loadedFromOnline?.[locale]) {
        if (!locales.length) return null;

        const currentLocale =
          locales?.find((l) => l.code.substring(0, 2) === locale) ||
          locales?.find((l) => l.code.substring(0, 2) === fallback);

        if (!currentLocale?.id) return null;

        const strings = await getTranslationsForLocale(currentLocale);
        setTranslations((prev) => ({
          ...prev,
          [currentLocale.code.substring(0, 2)]: strings,
        }));
        setLoadedFromOnline(s => ({ ...s, [locale]: true }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, locales]);

  const handleTranslations = useMemo(
    () => ({
      messages: translations[locale || fallback],
      locale,
    }),
    [translations, locale],
  );

  return handleTranslations;
};

export default useTranslations;
